import { useFlags } from '@atlaskit/flag';
import { RunsApiService } from 'core/api';
import { useDialog } from 'core/providers/DialogProvider';
import SharedButton from 'shared/components/buttons/button';
import PageHeader from 'shared/components/page-header/page-header';
import SharedTable from 'shared/components/table/table';
import { ISharedTableColumn, ISharedTableCustomCellTemplate } from 'shared/components/table/table-interface';
import ConfirmationDialog from 'modules/dialogs/confirmation-dialog';
import { useCollection } from 'core/hooks/useCollection';
import { deleteItem, deleteItems } from 'core/utilities/api-wrapper';
import { IRunDao } from 'core/api/types/runs.interface';
import { useNavigate } from 'react-router-dom';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import SharedFilters, { Filter, SelectedValue } from 'shared/components/filters/filters';
import { QueryConstraint, where } from 'firebase/firestore';
import Checkbox from '@atlaskit/checkbox';
import { PageElement } from 'core/utilities/interface-helpers';
import ExportDialog from 'modules/dialogs/export-dialog';
import { IButton } from 'shared/components/buttons/button-interface';
import AddEditRunsDialog from './add-edit-runs-dialog';
import { FirebaseFunctionNames } from 'core/constants/firebase-function-names';

const RunsTableColumns: ISharedTableColumn[] = [
  {
    label: 'Name',
    key: 'name',
    templateId: 'text',
    width: 10,
  },
  { label: 'Status', key: 'active', templateId: 'boolean', width: 10 },
  { label: '', key: 'action', templateId: 'action', width: 10 },
];

const RunsSettingList = () => {
  const dialog = useDialog();
  const flags = useFlags();
  const navigate = useNavigate();

  const [filterQuery, setFilterQuery] = useState<SelectedValue[]>([]);
  const [constraints, setConstraints] = useState<QueryConstraint[]>([]);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  const { tableRows, loading, loadMore, hasMore, loadingMore } = useCollection<IRunDao>({
    apiService: RunsApiService,
    constraints,
  });

  useEffect(() => {
    setSelectedRows([]);
  }, [tableRows]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const newConstraints = filterQuery
        .filter((filter) => filter.value !== '' && !filter.id.includes('.'))
        .map((filter) => {
          const value = filter.value === 'true' ? true : filter.value === 'false' ? false : filter.value;
          return where(filter.id, '==', value);
        });

      setConstraints(newConstraints);
    }, 500);
    return () => clearTimeout(timer);
  }, [filterQuery]);

  const handleFilterChange = useCallback(
    (values: SelectedValue[]) => {
      const currentSerialized = JSON.stringify(filterQuery.sort((a, b) => a.id.localeCompare(b.id)));
      const newSerialized = JSON.stringify(values.sort((a, b) => a.id.localeCompare(b.id)));

      if (currentSerialized !== newSerialized) {
        setFilterQuery(values);
      }
    },
    [filterQuery],
  );

  const rowChecked = (event: ChangeEvent<HTMLInputElement>, uid: string) => {
    if (event.target.checked) {
      setSelectedRows((prevState) => [...prevState, uid]);
    } else {
      setSelectedRows((prevState) => prevState.filter((selectedUid) => selectedUid !== uid));
    }
  };

  const selectCellTemplate = (run: IRunDao) => {
    return <Checkbox isChecked={selectedRows.includes(run.uid)} onChange={(e) => rowChecked(e, run.uid)} />;
  };

  const actionCellTemplate = (run: IRunDao) => (
    <div className='w-full flex justify-end space-x-2'>
      <SharedButton
        onClick={() => navigate(run.uid)}
        type='button'
        appearance='subtle'
        spacing='compact'
        label='View'
      />
      <SharedButton
        onClick={() => dialog?.openDialog(<AddEditRunsDialog run={run} />)}
        type='button'
        appearance='subtle'
        spacing='compact'
        label='Update'
      />
      <SharedButton
        onClick={() =>
          dialog?.openDialog(
            <ConfirmationDialog
              title='Delete run'
              textContent='Are you sure you want to delete this run? This action cannot be undone.'
              action={() => deleteItem(RunsApiService, run, flags, dialog)}
              buttonLabel='Delete Run'
            />,
          )
        }
        type='button'
        appearance='subtle'
        spacing='compact'
        label='Delete'
      />
    </div>
  );

  const customTemplates: ISharedTableCustomCellTemplate[] = [
    { template: selectCellTemplate, id: 'select' },
    {
      template: actionCellTemplate,
      id: 'action',
    },
  ];

  const headerActions: PageElement[] = [
    {
      key: 'exportRuns',
      element: (
        <SharedButton
          onClick={() =>
            dialog?.openDialog(
              <ExportDialog
                title='Export Runs'
                apiService={RunsApiService}
                functionName={FirebaseFunctionNames.EXPORT_RUNS}
                filterQuery={filterQuery}
              />,
            )
          }
          type='button'
          appearance='default'
          label='Export Runs'
        />
      ),
    },
    ...(selectedRows.length
      ? [
          {
            key: 'deleteRuns',
            element: (
              <SharedButton
                onClick={() =>
                  dialog?.openDialog(
                    <ConfirmationDialog
                      title='Delete runs'
                      textContent='Are you sure you want to delete these runs? This action cannot be undone.'
                      action={() => deleteItems(RunsApiService, selectedRows, flags, dialog)}
                      buttonLabel='Delete Runs'
                    />,
                  )
                }
                type='button'
                appearance='danger'
                label='Delete Runs'
              />
            ),
          },
        ]
      : [
          {
            key: 'addRun',
            element: (
              <SharedButton
                onClick={() => dialog?.openDialog(<AddEditRunsDialog />)}
                type='button'
                appearance='primary'
                label='Add Run'
              />
            ),
          },
        ]),
  ];

  const filters: Filter[] = [
    {
      id: 'active',
      label: 'Status',
      options: [
        { value: 'true', label: 'Active' },
        { value: 'false', label: 'Inactive' },
      ],
    },
  ];

  const loadMoreButton: IButton = {
    onClick: () => loadMore(),
    type: 'submit',
    appearance: 'subtle',
    label: 'Load more',
    isLoading: loadingMore,
  };

  return (
    <>
      <PageHeader title='Runs' actions={headerActions} />
      <SharedFilters filters={filters} onFilterValueChange={handleFilterChange} />
      <SharedTable
        columns={RunsTableColumns}
        customTemplates={customTemplates}
        rows={tableRows}
        isLoading={loading}
        loadMoreButton={hasMore ? loadMoreButton : undefined}
      />
    </>
  );
};

export default RunsSettingList;
